import { useRef, useState } from 'react';
import { api } from '../api';
import { fmtDate, fmtDayMonth } from '../lib/format';
import { eurC, parseEuro } from '../lib/money';
import { stundenPositionen, zeitraum, type KiPosition } from './stundenPositionen';
import { fehlerText } from './tarifHinweis';
import type { StundenVorschlag } from './types';

export type { KiPosition } from './stundenPositionen';

type Found = Extract<StundenVorschlag, { found: true }>;

const hours = (n: number) => n.toLocaleString('de-DE', { maximumFractionDigits: 2 });
const parseHours = (t: string) => {
  const v = Number(t.trim().replace(',', '.'));
  return Number.isFinite(v) && v > 0 && v <= 24 ? Math.round(v * 100) / 100 : null;
};

// Stundenzettel (Foto oder PDF) mit der KI lesen: Vorschlag gelb, der Mandant prüft Einträge und Stunden, trägt den
// Stundensatz ein und übernimmt sie als Positionen (je Eintrag eine oder alles in einer).
export function Stundenzettel({
  firmId,
  test,
  onTake,
  rateHint,
}: {
  firmId: string;
  // Testbetrieb: die KI läuft nicht nur in der EU, also nur Testdaten
  test: boolean;
  onTake(items: KiPosition[], from: string | null, to: string | null): void;
  // Stundensatz aus der letzten Rechnung für dieses Objekt: vorbelegt, mit Hinweis daneben
  rateHint?: { price: string; text: string } | null;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [v, setV] = useState<Found | null>(null);
  const [rows, setRows] = useState<{ on: boolean; stunden: string }[]>([]);
  const [rate, setRate] = useState('');
  const [mode, setMode] = useState<'einzeln' | 'summe'>('einzeln');
  // Namen der Mitarbeiter stehen sonst auf der Rechnung beim Kunden (3.6: nur, wenn der Kunde das verlangt)
  const [names, setNames] = useState(false);

  async function read(f: File) {
    setBusy(true);
    setError(null);
    setV(null);
    const fd = new FormData();
    fd.append('file', f, f.name);
    try {
      const res = await api.post<StundenVorschlag>(`/api/rechnungstool/${firmId}/stundenzettel`, fd);
      if (!res.found) setError(res.hinweis);
      else {
        setV(res);
        setRows(res.eintraege.map((e) => ({ on: true, stunden: hours(e.stunden) })));
        if (!rate.trim() && rateHint) setRate(rateHint.price);
      }
    } catch (err) {
      setError(fehlerText(err));
    } finally {
      setBusy(false);
      if (input.current) input.current.value = '';
    }
  }

  const picked = v ? v.eintraege.map((e, i) => ({ ...e, h: rows[i]!.on ? parseHours(rows[i]!.stunden) : null })) : [];
  const chosen = picked.filter((e, i) => rows[i]!.on);
  const total = chosen.reduce((s, e) => s + (e.h ?? 0), 0);
  const price = parseEuro(rate);
  const ok = chosen.length > 0 && chosen.every((e) => e.h != null) && typeof price === 'number' && price > 0;
  // netto wie auf der Rechnung: jede Position für sich gerundet
  const cents = ok ? Math.round((price as number) * 100) : 0;
  const net =
    mode === 'einzeln'
      ? chosen.reduce((s, e) => s + Math.round(e.h! * cents), 0)
      : Math.round((Math.round(total * 100) / 100) * cents);

  function take() {
    if (!ok) return;
    const zeilen = chosen.map((e) => ({ datum: e.datum, name: e.name, taetigkeit: e.taetigkeit, stunden: e.h! }));
    const { from, to } = zeitraum(zeilen);
    onTake(stundenPositionen(zeilen, { art: mode, preis: rate, namen: names }), from, to);
    setV(null);
  }

  return (
    <div className="rt-ki">
      <div className="rt-ki-head">
        <div>
          <b>Stundenzettel mit der KI lesen</b>
          <p className="muted">Foto oder PDF hochladen. Die KI liest Datum, Mitarbeiter, Tätigkeit und Stunden, Sie prüfen den Vorschlag.</p>
        </div>
        <button type="button" className="btn small" disabled={busy} onClick={() => input.current?.click()}>
          {busy ? 'KI liest …' : 'Stundenzettel hochladen'}
        </button>
        <input
          ref={input}
          type="file"
          hidden
          accept="application/pdf,image/jpeg,image/png,image/webp"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void read(f);
          }}
        />
      </div>
      {test && (
        <p className="rt-ki-test" role="note">
          Testbetrieb: Die KI läuft gerade nicht nur in der EU. Bitte nur Test-Stundenzettel hochladen, keine echten.
        </p>
      )}
      {error && <div className="notice warn">{error}</div>}
      {v && (
        <div className="rt-ki-res">
          {v.kunde && (
            <p className="rt-ki-note">
              Kunde / Objekt laut Zettel: <b>{v.kunde}</b>
            </p>
          )}
          {v.hinweis && <p className="rt-ki-note">{v.hinweis}</p>}
          <table className="rt-ki-tab">
            <thead>
              <tr>
                <th aria-label="Übernehmen" />
                <th>Datum</th>
                <th>Mitarbeiter</th>
                <th>Tätigkeit</th>
                <th className="r">Stunden</th>
              </tr>
            </thead>
            <tbody>
              {v.eintraege.map((e, i) => (
                <tr key={i} className={rows[i]!.on ? undefined : 'off'}>
                  <td>
                    <input
                      type="checkbox"
                      aria-label={`Eintrag ${i + 1} übernehmen`}
                      checked={rows[i]!.on}
                      onChange={(x) => setRows(rows.map((r, j) => (j === i ? { ...r, on: x.target.checked } : r)))}
                    />
                  </td>
                  <td>
                    {e.datum ? (
                      <>
                        <span className="rt-d-l">{fmtDate(e.datum)}</span>
                        <span className="rt-d-s">{fmtDayMonth(e.datum)}</span>
                      </>
                    ) : (
                      '–'
                    )}
                  </td>
                  <td>{e.name ?? '–'}</td>
                  <td>{e.taetigkeit ?? '–'}</td>
                  <td className="r">
                    <input
                      className="rt-ki-h"
                      inputMode="decimal"
                      aria-label={`Stunden Eintrag ${i + 1}`}
                      aria-invalid={rows[i]!.on && parseHours(rows[i]!.stunden) == null}
                      value={rows[i]!.stunden}
                      onChange={(x) => setRows(rows.map((r, j) => (j === i ? { ...r, stunden: x.target.value } : r)))}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={2}>Summe ({chosen.length} Einträge)</td>
                <td />
                <td />
                <td className="r num">{hours(total)} Std.</td>
              </tr>
            </tfoot>
          </table>
          <div className="rt-ki-take">
            <label className="field">
              <span>Stundensatz netto</span>
              <input value={rate} inputMode="decimal" placeholder="z. B. 32,50" onChange={(e) => setRate(e.target.value)} />
            </label>
            {rateHint && (
              <p className="rt-tip-line rt-tip-rate">
                Empfehlung: {rateHint.price} € je Std.
                <br />
                {rateHint.text}
              </p>
            )}
            <fieldset className="rt-ki-mode">
              <legend className="sr-only">Positionen</legend>
              <label>
                <input type="radio" checked={mode === 'einzeln'} onChange={() => setMode('einzeln')} /> Je Eintrag eine Position
              </label>
              <label>
                <input type="radio" checked={mode === 'summe'} onChange={() => setMode('summe')} /> Alles in einer Position
              </label>
            </fieldset>
          </div>
          {mode === 'einzeln' && v.eintraege.some((e) => e.name) && (
            <label className="check rt-ki-names">
              <input type="checkbox" checked={names} onChange={(e) => setNames(e.target.checked)} />
              <span>
                Namen der Mitarbeiter in die Positionen übernehmen
                <br />
                <small className="muted">Nur, wenn Ihr Kunde das verlangt, z. B. Stundenlohnzettel nach VOB/B.</small>
              </span>
            </label>
          )}
          <div className="rt-actions">
            <button type="button" className="btn" onClick={() => setV(null)}>
              Verwerfen
            </button>
            <button type="button" className="btn primary" disabled={!ok} onClick={take}>
              Übernehmen{ok ? ` (${eurC(net / 100)} netto)` : ''}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
